import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import { AddressInput, DatePickerNew, Input, Select } from 'common-src/components/base';
import { stateOptions } from 'common-src/constants/stateOptions';
import { createPatient, initialFlowOptions } from 'common-src/models/Patient';
import { cleanedText, PHONE_MASK } from 'common-src/presenters';
import { ColorsNew } from 'common-src/styles';

import usePopup from 'src/hooks/usePopup';
import BasePopup from 'src/popups/BasePopup';

import styles from './AddPatientPopup.module.scss';
import { initialState, sexOptions } from './constants';
import { catchErrors, getErrorMessage, getIsButtonEnabled } from './helpers';

const AddPatientPopup = ({ onClose }) => {
  const dispatch = useDispatch();

  const [data, setData] = useState(initialState);

  const { errors, setErrors, renderButtons } = usePopup();

  const handleInputChange = (field, value) => {
    setErrors((prev) => ({ ...prev, ...getErrorMessage(field, value) }));
    const dataCopy = JSON.parse(JSON.stringify(data));
    setData(_.set(dataCopy, field, value));
  };

  const onSubmitHandler = (loadingCallback, successCallback, errorCallback) => {
    const { errors, hasErrors } = catchErrors(data);
    if (hasErrors) {
      setErrors(errors);
      return;
    }

    loadingCallback();
    dispatch(
      createPatient(data, {
        successBlock: () => {
          successCallback('Patient created!');
          onClose(true);
        },
        errorBlock: (err) => errorCallback(err),
      }),
    );
  };

  return (
    <BasePopup
      id="add-patient"
      open
      onClose={onClose}
      title="Create Patient"
      customStyle={{ minWidth: '55vw' }}
    >
      <div className={styles.grid}>
        <Input
          id="first-name"
          label="First Name"
          errorText={errors.firstName}
          onTextChange={(value) => handleInputChange('firstName', value)}
          value={data.firstName}
          placeholder="Enter first name"
          required
          size="small"
          requiredColor={ColorsNew.mediumDarkRed}
        />
        <Input
          id="last-name"
          label="Last Name"
          errorText={errors.lastName}
          onTextChange={(value) => handleInputChange('lastName', value)}
          value={data.lastName}
          placeholder="Enter last name"
          required
          size="small"
          requiredColor={ColorsNew.mediumDarkRed}
        />
        <DatePickerNew
          id="date-of-birth"
          minDate={undefined}
          maxDate={new Date()}
          required
          errorText={errors.dob}
          placeholder="Choose date of birth"
          header="Date of birth"
          value={data.dob}
          onDateSelected={(date) =>
            handleInputChange('dob', date ? moment(date).format('YYYY-MM-DD') : null)
          }
          onBlur={(date) => {
            if (!date) {
              setErrors({ dob: 'Invalid date format' });
              return;
            }
            setErrors({ dob: '' });
          }}
          size="small"
          requiredColor={ColorsNew.mediumDarkRed}
        />
        <Select
          id="sex"
          errorText={errors.sex}
          label="Sex at birth"
          placeholder="Select sex"
          options={sexOptions}
          onChange={(op) => handleInputChange('sex', op.value)}
          value={sexOptions.find((op) => op.value === data.sex)}
          required
          position="absolute"
          size="small"
          requiredColor={ColorsNew.mediumDarkRed}
        />
        <Select
          id="initialFlow"
          errorText={errors.initialFlow}
          label="Patient Type"
          placeholder="Select initial flow"
          options={initialFlowOptions}
          onChange={(op) => handleInputChange('initialFlow', op.value)}
          value={initialFlowOptions.find((op) => op.value === data.initialFlow)}
          required
          position="absolute"
          size="small"
          requiredColor={ColorsNew.mediumDarkRed}
        />
        <Input
          id="email"
          classNames={[styles.width50]}
          label="Email"
          onTextChange={(value) => handleInputChange('email', value)}
          value={data.email}
          placeholder="Enter email"
          errorText={errors.email}
          size="small"
          requiredColor={ColorsNew.mediumDarkRed}
        />
        <Input
          id="phone-number"
          classNames={[styles.width50]}
          label="Phone Number"
          onTextChange={(value) => {
            handleInputChange('phoneNumber', cleanedText(value));
          }}
          value={data.phoneNumber}
          placeholder="Enter phone number"
          mask={PHONE_MASK}
          required
          size="small"
          errorText={errors.phoneNumber}
          requiredColor={ColorsNew.mediumDarkRed}
        />
        <AddressInput
          id="address-street1"
          classNames={[styles.width50]}
          errorText={errors['address.street1']}
          label="Address 1"
          placeholder="Enter street address"
          value={data.address.street1}
          onChange={(addressInfo) => handleInputChange('address', addressInfo)}
          onTextChange={(text) => {
            handleInputChange('address.street1', text);
          }}
          required
          size="small"
          requiredColor={ColorsNew.mediumDarkRed}
        />
        <Input
          id="address-street2"
          classNames={[styles.width50]}
          errorText={errors['address.street2']}
          label="Address 2"
          onTextChange={(value) => handleInputChange('address.street2', value)}
          value={data.address.street2}
          placeholder="Enter apt, unit, ste, etc."
          size="small"
          requiredColor={ColorsNew.mediumDarkRed}
        />
        <Input
          id="address-city"
          errorText={errors['address.city']}
          label="City"
          onTextChange={(value) => handleInputChange('address.city', value)}
          value={data.address.city}
          placeholder="Enter city"
          required
          size="small"
          requiredColor={ColorsNew.mediumDarkRed}
        />
        <Select
          id="address-state"
          errorText={errors['address.state']}
          label="State"
          placeholder="Select state"
          options={stateOptions}
          onChange={(op) => handleInputChange('address.state', op.value)}
          value={stateOptions.find((op) => op.value === data.address.state)}
          paperHeight={350}
          isSearchable
          required
          size="small"
          position="absolute"
          requiredColor={ColorsNew.mediumDarkRed}
        />
        <Input
          id="address-zip-code"
          errorText={errors[`address.zipcode`]}
          label="Zip Code"
          onTextChange={(value) => handleInputChange('address.zipcode', value)}
          value={data.address.zipcode}
          placeholder="Enter zip code"
          required
          size="small"
          requiredColor={ColorsNew.mediumDarkRed}
        />
      </div>
      {renderButtons({
        containerClassName: styles.buttonsContainer,
        onClose,
        onSubmit: onSubmitHandler,
        isSubmitEnabled: getIsButtonEnabled(data, errors),
        submitButtonText: 'Save and close',
      })}
    </BasePopup>
  );
};

AddPatientPopup.propTypes = {
  onClose: PropTypes.func,
};

export default AddPatientPopup;
